import React from 'react';
import { Phone, Clock, MapPin } from 'lucide-react';

const TarangireNgorongoro = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">3 DAYS TARANGIRE & NGORONGORO CRATER SAFARI</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          3 Days Tarangire & Ngorongoro Crater Safari – Tanzania Wilderness Safari Tour
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Embark on an exciting <strong>3 Days Tarangire & Ngorongoro Crater Safari</strong> adventure in Tanzania. Experience the thrill of observing wildlife in their natural habitat, surrounded by diverse landscapes. Discover the wonders of Tarangire National Park, famed for its large elephant herds and iconic baobab trees, and the magnificent Ngorongoro Crater, a UNESCO World Heritage Site renowned as one of Africa's premier natural wonders, hosting a spectacular array of fauna.
              </p>
            </div>

            {/* Placeholder for Safari Image */}
            <div className="mb-8">
              <div className="bg-[#f5f5f5] h-64 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                <img 
                src="/44.jpg" 
                alt="3 Days Tanzania Wildlife Safari" 
                className="w-full h-48 object-cover"
              />
              </div>
              <p className="text-center text-xs text-gray-500 mt-2">
                Tarangire & Ngorongoro Crater Safari - African sunset landscape
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 3 Days Tarangire & Ngorongoro Crater Safari
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Arrival in Arusha</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Our driver guide and team will warmly welcome you at the airport and transfer you to your lodging in Arusha. Depending on your arrival time, you can unwind, explore this charming city with a sightseeing tour, and prepare for the adventure ahead.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Mountain View Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Tarangire National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Your safari adventure begins with a morning pickup from your accommodation in Arusha, followed by a scenic 2-hour drive to Tarangire National Park. Famed for its large elephant herds and iconic baobab trees, Tarangire is centered around the Tarangire River, a vital water source attracting diverse wildlife such as antelopes, bushbucks, and gazelles. In the afternoon, enjoy a game drive where you may spot vibrant birds, zebras, and predators like lions and leopards, guided by your expert driver. Later, head to your lodge in Karatu for a delightful dinner and overnight stay.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Kubu Kubu Tented Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Ngorongoro Crater & Transfer back to Arusha</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Start the day early with a descent into the <strong>Ngorongoro Conservation Area</strong>, a UNESCO World Heritage Site and one of Africa’s premier natural wonders. The vast ancient caldera teems with over 25,000 animals, including the Big Five—lions, elephants, buffaloes, rhinos, and leopards—alongside wildebeest, hyenas, and numerous bird species. After an extraordinary game drive and a picnic lunch within the crater, begin your journey back to Arusha, arriving by early evening.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Transportation via 4x4 safari vehicle with a pop-up roof
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodations and meals as stated
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Game drives as per itinerary
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Entrance fees to National Parks
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    En-route lunches
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking driver guide
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Visa Fees & flights
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Laundry
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Alcoholic drinks
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International vaccinations & tests
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Health test requirements
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">3 Days Tarangire & Ngorongoro Crater Safari</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-600" />
                    Duration: 3 Days, 2 Nights safari
                  </li>
                  <li className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-gray-600" />
                    Regions / Arusha
                  </li>
                  <li className="text-xs text-gray-600 ml-6">
                    Hotel pickup - Your side returned to your hotel
                  </li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TarangireNgorongoro;